import { Link } from '@tanstack/react-router'
import { useMemo, useState } from 'react'

import { ChevronRightIcon, SearchIcon } from '~/components/icons'
import { PageDialog } from '~/components/page-dialog'
import { useDataState } from '~/data/data-context'
import { formatDayLabel } from '~/lib/dates'
import { withoutDialog } from '~/lib/dialogs'
import { effectiveRecentExercises, kindOf, type ExerciseKind } from '~/lib/exercise-insights'

const KIND_LABELS: { kind: ExerciseKind; label: string }[] = [
  { kind: 'lift', label: 'Lift' },
  { kind: 'bodyweight', label: 'Bodyweight' },
  { kind: 'hold', label: 'Hold' },
  { kind: 'run', label: 'Run' },
]

/** Every exercise ever done, most recently done first, narrowed by name and by logging method. */
export function ExercisesDialog({ onClose }: { onClose: () => void }) {
  const state = useDataState()
  const [query, setQuery] = useState('')
  const [chosen, setChosen] = useState<ExerciseKind | null>(null)

  const exercises = useMemo(
    () => effectiveRecentExercises(state.recentExercises, state.sets),
    [state.recentExercises, state.sets],
  )

  /*
   * Only the methods that have actually been logged. A filter for a method you have never
   * used is a filter whose one answer is nothing, and four of them where two would do says
   * the app expects a kind of training of you rather than describing the training you did.
   */
  const kinds = useMemo(
    () => KIND_LABELS.filter(({ kind }) => exercises.some((it) => kindOf(it.records) === kind)),
    [exercises],
  )
  // A method chosen and then left with nothing behind it falls away rather than emptying the page.
  const kind = kinds.some((it) => it.kind === chosen) ? chosen : null

  const wanted = query.trim().toLowerCase()
  const shown = useMemo(
    () =>
      exercises.filter(
        (exercise) =>
          (wanted === '' || exercise.name.toLowerCase().includes(wanted)) &&
          (kind === null || kindOf(exercise.records) === kind),
      ),
    [exercises, wanted, kind],
  )

  return (
    <PageDialog title="Exercises" onClose={onClose}>
      <div className="space-y-6">
        <label className="flex items-center gap-3 rounded-full bg-surface-raised px-4 py-2.5 focus-within:ring-2 focus-within:ring-accent">
          <SearchIcon className="h-6 w-6 shrink-0 text-ink-muted" />
          <span className="sr-only">Search exercises</span>
          <input
            type="search"
            value={query}
            placeholder="Search exercises"
            onChange={(event) => {
              setQuery(event.target.value)
            }}
            className="flex-1 bg-transparent text-ink outline-none placeholder:text-ink-muted"
          />
        </label>

        {/* One method is not a choice, so the row only earns its place where there are two. */}
        {kinds.length > 1 && (
          <div className="flex flex-wrap gap-2" role="group" aria-label="Logging methods">
            {kinds.map(({ kind: option, label }) => (
              <button
                key={option}
                type="button"
                aria-pressed={kind === option}
                // Choosing the one already chosen clears it, so there is always a way back to
                // everything without a control of its own saying "all".
                onClick={() => {
                  setChosen(kind === option ? null : option)
                }}
                className={`flex h-8 items-center rounded-full px-3.5 text-sm transition-colors ${
                  kind === option
                    ? 'bg-accent/15 text-accent ring-1 ring-accent/40 ring-inset'
                    : 'text-ink-muted ring-1 ring-white/15 ring-inset hover:bg-hover hover:text-ink'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {shown.length === 0 ? (
          <p className="py-16 text-center text-ink-muted">
            {exercises.length === 0
              ? 'No exercises yet. Log a set and it lands here.'
              : 'No exercise matches that.'}
          </p>
        ) : (
          <ul>
            {shown.map((exercise) => (
              <li key={exercise.name}>
                <Link
                  to="/exercises/$exerciseName"
                  params={{ exerciseName: exercise.name }}
                  search={withoutDialog}
                  className="group flex items-center gap-3 rounded-2xl px-2 py-3 transition-colors hover:bg-hover"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium text-ink">{exercise.name}</span>
                    <span className="block truncate text-sm text-ink-muted">
                      {KIND_LABELS.find(({ kind: it }) => it === kindOf(exercise.records))?.label} ·{' '}
                      {formatDayLabel(exercise.lastPerformedAt)}
                    </span>
                  </span>
                  <ChevronRightIcon className="h-6 w-6 shrink-0 text-ink-muted transition-transform duration-150 group-hover:translate-x-1" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </PageDialog>
  )
}
